import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { videosAPI, tiktokAPI, analyticsAPI, getAssetUrl, getVideoUrl, getThumbnailUrl, captionsAPI, BASE_URL, batchExportAPI, userTemplatesAPI } from "../lib/api";
import { EndingFramePicker } from "../components/EndingFramePicker";
import { SeriesDialog } from "../components/series/SeriesDialog";
import { ImproveScenePanel } from "../components/series/ImproveScenePanel";
import { groupSeries } from "../lib/series";
import { formatDate, getStatusColor } from "../lib/utils";
import { useVideoNotifications } from "../hooks/useVideoNotifications";
import { useAuthStore } from "../lib/store";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Film,
  Filter,
  Trash2,
  Eye,
  Hash,
  Calendar,
  FileText,
  MoreVertical,
  Loader2,
  Download,
  Play,
  RefreshCw,
  Send,
  Share2,
  Copy,
  Check,
  X,
  ExternalLink,
  Captions,
  Bell,
  TrendingUp,
  Type,
  Sparkles,
  Users,
  AlertTriangle,
  Info,
  Wand2,
  Package,
  CheckSquare,
  Square,
  Bookmark,
  Save,
} from "lucide-react";
import VideoDownloadDialog from "../components/VideoDownloadDialog";
import { ApplyCaptionsModal } from "../components/ApplyCaptionsModal";
import { TikTokShareDialog } from "../components/TikTokShareDialog";
import ContentPolicyModal from "../components/ContentPolicyModal";

// TikTok Logo Component
const TikTokLogo = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

// Social Media Icons
const TwitterIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const FacebookIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const WhatsAppIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const LinkedInIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const TelegramIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
  </svg>
);

const InstagramIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z" />
  </svg>
);

const RedditIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
  </svg>
);
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1 },
};

// Helper to check if a video file is likely unavailable (legacy video without cloud storage)
// Videos are considered unavailable if:
// - Status is not 'generating' or 'failed'
// - No cloud_video_url (S3)
// - No fal_video_url (AI output)
// - video_url is a local path (starts with /app or generated_videos) OR is missing
const isVideoUnavailable = (video) => {
  if (!video) return true;
  // Still generating or already marked as failed - not "unavailable"
  if (video.status === "generating" || video.status === "queued" || video.status === "failed") return false;
  // Has cloud URL - available
  if (video.cloud_video_url) return false;
  // Has fal URL - available
  if (video.fal_video_url) return false;
  // Has external URL - available
  if (video.video_url?.startsWith('http')) return false;
  // Local path or no URL - unavailable
  return true;
};

export default function VideoLibraryPage({ embedded = false }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [filter, setFilter] = useState("all");
  const [createdForClientFilter, setCreatedForClientFilter] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [postingToTikTok, setPostingToTikTok] = useState(null);
  const [videoProgress, setVideoProgress] = useState({});
  const [shareVideo, setShareVideo] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedHashtags, setCopiedHashtags] = useState(false);
  const videosRef = useRef([]);
  const completedVideosRef = useRef(new Set()); // Track videos we've already notified about
  
  // Batch export state
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedVideoIds, setSelectedVideoIds] = useState(new Set());
  const [isExporting, setIsExporting] = useState(false);
  
  // Save as Template state
  const [saveTemplateVideo, setSaveTemplateVideo] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [templateIsPublic, setTemplateIsPublic] = useState(false);
  
  // Get user from auth store
  const { user } = useAuthStore();
  const isAccountManager = user?.role === "account_manager" || user?.role === "admin" || user?.is_admin;
  
  // Video notifications hook
  const { 
    requestNotificationPermission, 
    notifyVideoComplete, 
    notifyVideoFailed,
    updateTabTitle,
    isNotificationSupported,
    notificationPermission 
  } = useVideoNotifications();
  
  // Download dialog state
  const [downloadDialogVideo, setDownloadDialogVideo] = useState(null);
  
  // TikTok OAuth share dialog state
  const [tiktokShareVideo, setTiktokShareVideo] = useState(null);
  
  // TikTok multi-account posting state (legacy - keeping for backwards compatibility)
  const [tiktokAccounts, setTiktokAccounts] = useState([]);
  const [selectedTiktokAccount, setSelectedTiktokAccount] = useState(null);
  const [postToTiktokDialog, setPostToTiktokDialog] = useState(null); // video to post
  const [tiktokAccountsLoading, setTiktokAccountsLoading] = useState(false);
  
  // Retry state
  const [retrying, setRetrying] = useState(null);
  
  // URL refresh state
  const [refreshingUrl, setRefreshingUrl] = useState(null);
  
  // Function to refresh video URL when it expires
  const refreshVideoUrl = async (videoId) => {
    try {
      setRefreshingUrl(videoId);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BASE_URL}/api/videos/${videoId}/refresh-url`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Update the video in local state
        setVideos(prev => prev.map(v => 
          v.id === videoId 
            ? { ...v, video_url: data.video_url, thumbnail_url: data.thumbnail_url || v.thumbnail_url }
            : v
        ));
        if (selectedVideo?.id === videoId) {
          setSelectedVideo(prev => ({ 
            ...prev, 
            video_url: data.video_url, 
            thumbnail_url: data.thumbnail_url || prev.thumbnail_url 
          }));
        }
        toast.success('Video URL refreshed');
        return true;
      } else {
        toast.error('Failed to refresh video URL');
        return false;
      }
    } catch (error) {
      console.error('[VideoLibrary] URL refresh error:', error);
      toast.error('Failed to refresh video URL');
      return false;
    } finally {
      setRefreshingUrl(null);
    }
  };
  
  // Captions modal state
  const [captionsModalVideo, setCaptionsModalVideo] = useState(null);
  
  // Viral score analysis state
  const [analyzingViralScore, setAnalyzingViralScore] = useState(false);
  const [viralScoreResult, setViralScoreResult] = useState(null);
  
  // Enhanced video creation state
  const [selectedSuggestions, setSelectedSuggestions] = useState([]);
  const [keepSameVoice, setKeepSameVoice] = useState(true);
  const [keepSameMusic, setKeepSameMusic] = useState(true);
  const [useMotionVideo, setUseMotionVideo] = useState(false);
  const [creatingEnhancedVideo, setCreatingEnhancedVideo] = useState(false);
  const [predictedNewScore, setPredictedNewScore] = useState(null);

  // Content Policy Modal state
  const [contentPolicyModalOpen, setContentPolicyModalOpen] = useState(false);
  const [contentPolicyAnalysis, setContentPolicyAnalysis] = useState(null);
  const [contentPolicyVideoId, setContentPolicyVideoId] = useState(null);

  // Story Continuation Modal state
  const [showContinueStoryModal, setShowContinueStoryModal] = useState(false);
  const [storyAnalysis, setStoryAnalysis] = useState(null);
  const [analyzingStory, setAnalyzingStory] = useState(false);
  const [continuationPrompt, setContinuationPrompt] = useState("");
  const [generatingContinuation, setGeneratingContinuation] = useState(false);
  const [lastFramePreview, setLastFramePreview] = useState(null);
  const [endingFrames, setEndingFrames] = useState([]);
  const [seriesInfo, setSeriesInfo] = useState(null);
  const [chosenSuggestion, setChosenSuggestion] = useState(null);
  const [openSeriesId, setOpenSeriesId] = useState(null);
  const seriesView = useMemo(() => groupSeries(videos), [videos]);

  // Domain for share links
  const DOMAIN = "https://igoviralcreator.io";

  // Keep videosRef in sync with videos state
  useEffect(() => {
    videosRef.current = videos;
  }, [videos]);
  
  // Handle video query parameter - auto-open video modal when navigating from dashboard
  useEffect(() => {
    const videoId = searchParams.get("video");
    if (videoId && videos.length > 0) {
      const video = videos.find(v => v.id === videoId);
      if (video) {
        setSelectedVideo(video);
        // Clear the URL parameter after opening the modal
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, videos, setSearchParams]);
  
  // Fetch TikTok accounts on mount
  useEffect(() => {
    const fetchTiktokAccounts = async () => {
      try {
        const response = await tiktokAPI.getAccounts();
        setTiktokAccounts(response.data || []);
        // Auto-select default account
        const defaultAcc = response.data?.find(a => a.is_default);
        if (defaultAcc) {
          setSelectedTiktokAccount(defaultAcc);
        } else if (response.data?.length > 0) {
          setSelectedTiktokAccount(response.data[0]);
        }
      } catch (error) {
        console.error("Failed to fetch TikTok accounts:", error);
      }
    };
    fetchTiktokAccounts();
  }, []);

  const fetchVideos = useCallback(async (retryCount = 0) => {
    const maxRetries = 4;
    const timeoutMs = 30000; // 30 second timeout (increased for slow connections)
    const retryDelays = [1000, 2000, 3000, 5000]; // Progressive retry delays
    
    // Only show loading on first attempt to avoid flickering
    if (retryCount === 0) {
      setLoading(true);
    }
    
    try {
      const status = filter === "all" ? null : filter;
      console.log(`[VIDEO LIBRARY] Fetching videos (attempt ${retryCount + 1}/${maxRetries + 1}), filter: ${status}`);
      
      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), timeoutMs);
      });
      
      // Race between fetch and timeout
      const response = await Promise.race([
        videosAPI.getVideos(status, createdForClientFilter),
        timeoutPromise
      ]);
      
      // Only update if we got valid data
      if (response?.data) {
        console.log(`[VIDEO LIBRARY] Successfully loaded ${response.data.length} videos`);
        setVideos(response.data);
        setFetchError(false);
      }
    } catch (error) {
      console.error("[VIDEO LIBRARY] Failed to fetch videos:", error);
      console.error("[VIDEO LIBRARY] Error details:", error.response?.status, error.response?.data, error.code);
      
      // Check for auth errors - don't clear data, user needs to re-login
      if (error.response?.status === 401) {
        console.warn("[VIDEO LIBRARY] Auth error - session may have expired");
        setFetchError(true);
        setLoading(false);
        return;
      }
      
      // Retry on timeout, network errors, or 5xx server errors
      const isRetryableError = 
        error.message === 'Request timeout' || 
        error.code === 'ERR_NETWORK' || 
        error.code === 'ECONNABORTED' ||
        (error.response?.status >= 500 && error.response?.status < 600);
      
      if (retryCount < maxRetries && isRetryableError) {
        const delay = retryDelays[retryCount] || 5000;
        console.log(`[VIDEO LIBRARY] Retrying fetch in ${delay}ms (attempt ${retryCount + 2}/${maxRetries + 1})...`);
        setTimeout(() => fetchVideos(retryCount + 1), delay);
        return;
      }
      
      // Mark error state after all retries exhausted
      console.error(`[VIDEO LIBRARY] All ${maxRetries + 1} attempts failed`);
      setFetchError(true);
      
      // Only clear videos if this is not a transient error and we have no data yet
      // This prevents blanking existing data on temporary failures
      if (videos.length === 0) {
        setVideos([]);
      }
    }
    setLoading(false);
  }, [filter, createdForClientFilter, videos.length]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  // Poll for generating videos to check if complete
  useEffect(() => {
    let interval = null;
    
    // Update tab title if any videos are generating
    const generatingCount = videos.filter(v => v.status === "generating" || v.status === "queued").length;
    if (generatingCount > 0) {
      updateTabTitle('generating');
    }
    
    const startPolling = () => {
      interval = setInterval(async () => {
        const currentVideos = videosRef.current;
        const generatingVideos = currentVideos.filter(v => v.status === "generating" || v.status === "queued");
        
        if (generatingVideos.length === 0) {
          return;
        }
        
        const newProgress = {};
        let anyComplete = false;
        let completedVideoTitle = '';
        let anyFailed = false;
        let failedVideoTitle = '';
        let failedError = '';
        
        for (const video of generatingVideos) {
          try {
            const response = await videosAPI.getVideoProgress(video.id);
            newProgress[video.id] = response.data;
            
            // Check for completion and trigger notification (only once per video)
            if (response.data.stage === "complete" && !completedVideosRef.current.has(video.id)) {
              anyComplete = true;
              completedVideoTitle = video.title || 'Your video';
              completedVideosRef.current.add(video.id);
            }
            
            // Check for failure
            if (response.data.stage === "failed" && !completedVideosRef.current.has(video.id)) {
              anyFailed = true;
              failedVideoTitle = video.title || 'Your video';
              failedError = response.data.error || '';
              completedVideosRef.current.add(video.id);
            }
          } catch (error) {
            // Log polling errors but don't disrupt the user
            console.warn(`[VideoLibrary] Polling error for video ${video.id}:`, error.message || error);
          }
        }
        
        setVideoProgress(prev => ({ ...prev, ...newProgress }));
        
        // Trigger notifications for completed videos
        if (anyComplete) {
          notifyVideoComplete(completedVideoTitle);
          toast.success(`🎬 "${completedVideoTitle}" is ready!`, { duration: 4000 });
        }
        
        if (anyFailed) {
          notifyVideoFailed(failedVideoTitle, failedError);
          toast.error(`❌ "${failedVideoTitle}" failed to generate`, { duration: 4000 });
        }
        
        if (anyComplete || anyFailed) {
          // Update local video state with thumbnail/video URLs from progress response
          setVideos(prevVideos => {
            return prevVideos.map(video => {
              const progress = newProgress[video.id];
              if (progress && progress.stage === "complete") {
                return {
                  ...video,
                  status: "pending",
                  thumbnail_url: progress.thumbnail_url || video.thumbnail_url,
                  video_url: progress.video_url || video.video_url,
                  srt_url: progress.srt_url || video.srt_url
                };
              }
              return video;
            });
          });
          
          // Wait a moment for DB to fully update, then fetch fresh data
          setTimeout(() => {
            fetchVideos();
          }, 1500);
        }
      }, 8000); // QUICK WIN #2: Reduced from 3s to 8s to reduce server load during generation
    };

    startPolling();

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [fetchVideos, videos, notifyVideoComplete, notifyVideoFailed, updateTabTitle]);

  // Open download dialog instead of directly downloading
  const handleDownload = (video) => {
    setDownloadDialogVideo(video);
  };

  // Batch export functions
  const toggleSelectMode = () => {
    setIsSelectMode(!isSelectMode);
    if (isSelectMode) {
      setSelectedVideoIds(new Set());
    }
  };

  const toggleVideoSelection = (videoId) => {
    setSelectedVideoIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(videoId)) {
        newSet.delete(videoId);
      } else {
        newSet.add(videoId);
      }
      return newSet;
    });
  };

  const selectAllVideos = () => {
    // Filter videos based on current filter setting and select only completed ones
    const completedVideoIds = videos
      .filter(v => {
        // Apply same filter logic as the video list
        if (filter === "all") return true;
        return v.status === filter;
      })
      .filter(v => v.status === 'completed' && (v.cloud_video_url || v.video_url))
      .map(v => v.id);
    setSelectedVideoIds(new Set(completedVideoIds));
  };

  const handleBatchExport = async () => {
    if (selectedVideoIds.size === 0) return;
    
    if (selectedVideoIds.size > 25) {
      toast.error('Maximum 25 videos per export batch');
      return;
    }
    
    setIsExporting(true);
    const loadingToast = toast.loading(`Preparing ${selectedVideoIds.size} video(s) for download...`);
    
    try {
      const response = await batchExportAPI.exportVideos(Array.from(selectedVideoIds));
      
      if (response.data.success && response.data.download_url) {
        toast.dismiss(loadingToast);
        
        toast.success(
          <div className="flex flex-col gap-2">
            <span>ZIP ready! {response.data.videos_included} video(s) included</span>
            <a 
              href={response.data.download_url}
              download
              className="text-[#00F0FF] underline hover:text-[#00F0FF]/80 flex items-center gap-1"
              onClick={() => toast.dismiss()}
            >
              <Download className="w-4 h-4" />
              Download ZIP
            </a>
            {response.data.videos_failed > 0 && (
              <span className="text-xs text-amber-500">
                {response.data.videos_failed} video(s) could not be included
              </span>
            )}
          </div>,
          { duration: 15000 }
        );
        
        window.open(response.data.download_url, '_blank');
        
        setSelectedVideoIds(new Set());
        setIsSelectMode(false);
      } else {
        toast.dismiss(loadingToast);
        toast.error('Failed to create export');
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Batch export error:', error);
      toast.error(error.response?.data?.detail || 'Failed to export videos');
    } finally {
      setIsExporting(false);
    }
  };

  // Save video as template
  const handleOpenSaveTemplate = (video) => {
    setSaveTemplateVideo(video);
    setTemplateName(video.title || "My Viral Template");
    setTemplateDescription(`Template based on "${video.title || 'video'}"`);
    setTemplateIsPublic(false);
  };

  const handleSaveAsTemplate = async () => {
    if (!saveTemplateVideo) return;
    if (!templateName.trim()) {
      toast.error("Please enter a template name");
      return;
    }

    setSavingTemplate(true);
    try {
      const video = saveTemplateVideo;
      
      // Extract settings from the video
      const templateData = {
        name: templateName.trim(),
        description: templateDescription.trim(),
        category: "Custom",
        script: video.script || video.prompt || "",
        visual_description: video.visual_description || video.visualDescription || "",
        duration: parseInt(video.duration) || 15,
        voice_option: video.voice_option || video.voiceOption || "none",
        video_mode: video.video_mode || video.videoMode || "fast",
        video_model: video.video_model || video.videoModel || "kling",
        quality_tier: video.quality_tier || video.qualityTier || "turbo",
        hashtags: video.hashtags || [],
        is_public: templateIsPublic,
      };

      await userTemplatesAPI.createTemplate(templateData);
      
      toast.success("Template saved!", {
        description: "You can now use this template to create similar videos",
        action: {
          label: "View Templates",
          onClick: () => navigate("/templates"),
        },
      });
      
      setSaveTemplateVideo(null);
      setTemplateName("");
      setTemplateDescription("");
    } catch (error) {
      console.error("Failed to save template:", error);
      toast.error(error.response?.data?.detail || "Failed to save template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDelete = async (videoId) => {
    setDeleting(videoId);
    try {
      await videosAPI.deleteVideo(videoId);
      toast.success("Video deleted", { duration: 3000 });
      setVideos((prev) => prev.filter((v) => v.id !== videoId));
    } catch (error) {
      toast.error("Failed to delete video", { duration: 4000 });
    } finally {
      setDeleting(null);
    }
  };

  const handleRetryVideo = async (videoId) => {
    setRetrying(videoId);
    try {
      const response = await videosAPI.retryVideo(videoId);
      if (response.data.success) {
        toast.success("Video retry started!", {
          description: "Your video is being regenerated.",
        });
        // Update video status locally
        setVideos((prev) =>
          prev.map((v) =>
            v.id === videoId
              ? { ...v, status: "generating", progress_percent: 5, error: null }
              : v
          )
        );
        // Close modal if open
        if (selectedVideo?.id === videoId) {
          setSelectedVideo((prev) => ({ ...prev, status: "generating", progress_percent: 5, error: null }));
        }
      } else {
        toast.error(response.data.error || "Failed to retry video");
      }
    } catch (error) {
      console.error("Retry error:", error);
      toast.error(error.response?.data?.error || "Failed to retry video");
    } finally {
      setRetrying(null);
    }
  };

  const handleRetryNoVoice = async (videoId) => {
    setRetrying(videoId);
    try {
      const response = await videosAPI.retryVideoNoVoice(videoId);
      if (response.data.success) {
        toast.success("Video retry started WITHOUT voiceover!", {
          description: "This will skip the voiceover step to avoid timeout issues.",
        });
        // Update video status locally
        setVideos((prev) =>
          prev.map((v) =>
            v.id === videoId
              ? { ...v, status: "generating", progress_percent: 5, error: null, voice_option: "none" }
              : v
          )
        );
        if (selectedVideo?.id === videoId) {
          setSelectedVideo((prev) => ({ ...prev, status: "generating", progress_percent: 5, error: null }));
        }
      } else {
        toast.error(response.data.error || "Failed to retry video");
      }
    } catch (error) {
      console.error("Retry no voice error:", error);
      toast.error(error.response?.data?.error || "Failed to retry video");
    } finally {
      setRetrying(null);
    }
  };

  const handlePostToTikTok = async (video) => {
    // Open the TikTok OAuth share dialog
    setTiktokShareVideo(video);
  };
  
  // Legacy mock function - keeping for backwards compatibility
  const confirmPostToTikTok = async () => {
    if (!postToTiktokDialog || !selectedTiktokAccount) return;
    
    setPostingToTikTok(postToTiktokDialog.id);
    setPostToTiktokDialog(null);
    
    // Simulate posting delay (MOCKED - in production this would call real API)
    setTimeout(() => {
      toast.success(`Video queued for posting to @${selectedTiktokAccount.tiktok_username}!`, {
        description: "Your video will be posted shortly. (Demo Mode)",
      });
      setPostingToTikTok(null);
    }, 1500);
  };

  // Share functionality
  const getShareUrl = (video) => {
    return `${DOMAIN}/v/${video.id}`;
  };

  const getShareText = (video) => {
    const hashtags = video.hashtags?.slice(0, 5).join(" ") || "";
    return `Check out my viral video "${video.title}" created with iGoViral! ${hashtags}`;
  };

  const handleCopyLink = async (video) => {
    const shareUrl = getShareUrl(video);
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopiedLink(true);
      toast.success("Link copied to clipboard!", { duration: 3000 });
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (error) {
      // Fallback for mobile
      const textArea = document.createElement("textarea");
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopiedLink(true);
      toast.success("Link copied to clipboard!", { duration: 3000 });
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  const shareLinks = (video) => {
    const shareUrl = encodeURIComponent(getShareUrl(video));
    const shareText = encodeURIComponent(getShareText(video));
    const title = encodeURIComponent(video.title);

    return {
      twitter: `https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}&quote=${shareText}`,
      whatsapp: `https://api.whatsapp.com/send?text=${shareText}%20${shareUrl}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`,
      telegram: `https://t.me/share/url?url=${shareUrl}&text=${shareText}`,
      reddit: `https://reddit.com/submit?url=${shareUrl}&title=${title}`,
    };
  };

  // Story Continuation Functions
  const analyzeVideoForContinuation = async (video) => {
    setAnalyzingStory(true);
    setStoryAnalysis(null);
    setLastFramePreview(null);
    
    try {
      const response = await fetch(`${BASE_URL}/api/videos/${video.id}/analyze-for-continuation`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Analysis failed");
      }
      
      const result = await response.json();
      setStoryAnalysis(result.analysis);
      setChosenSuggestion(null);
      setLastFramePreview(result.last_frame);
      setEndingFrames(result.ending_frames || []);
      setSeriesInfo({ title: result.series_title || video.title, next: result.next_episode_number || 2, settings: result.settings || null });
      
      // Pre-fill with first suggestion's prompt if available
      if (result.analysis?.continuation_suggestions?.length > 0) {
        const firstSuggestion = result.analysis.continuation_suggestions[0];
        // Handle both old format (string) and new format (object with prompt)
        const promptText = typeof firstSuggestion === 'string' 
          ? firstSuggestion 
          : firstSuggestion.prompt || firstSuggestion.premise || '';
        setContinuationPrompt(promptText);
        setChosenSuggestion(typeof firstSuggestion === 'object' ? firstSuggestion : null);
      }
      
      toast.success("Video analyzed! Now describe what happens next.");
    } catch (error) {
      toast.error(error.message || "Failed to analyze video");
    } finally {
      setAnalyzingStory(false);
    }
  };
  
  const generateStoryContinuation = async () => {
    if (!selectedVideo || !continuationPrompt.trim()) {
      toast.error("Please select or write a continuation prompt");
      return;
    }
    
    // Get the selected suggestion details if available
    const selectedSuggestion = storyAnalysis?.continuation_suggestions?.find(s => {
      const promptText = typeof s === 'string' ? s : (s.prompt || s.premise);
      return promptText === continuationPrompt;
    });
    
    const picked = typeof selectedSuggestion === 'object' ? selectedSuggestion : chosenSuggestion;
    const subtitle = (picked?.title || "")
      .replace(/^\s*episode\s*\d+\s*[:\-–]\s*/i, "").trim();
    // Build continuation data to pass to Create Video page
    const continuationData = {
      // Pre-fill the prompt/script with the continuation premise
      prefillScript: continuationPrompt,
      prefillTitle: `${seriesInfo?.title || selectedVideo.title} - Episode ${seriesInfo?.next || 2}${subtitle ? `: ${subtitle}` : ""}`,
      continuationSubtitle: subtitle,
      originalSettings: seriesInfo?.settings || null,
      
      // Pass original video context for reference
      continuationOf: {
        videoId: selectedVideo.id,
        videoTitle: selectedVideo.title,
        lastFrame: lastFramePreview, // Hidden - used by backend for visual consistency
        originalPrompt: selectedVideo.prompt || selectedVideo.script,
      },
      
      // Pass analysis data for AI assistant context
      storyAnalysis: storyAnalysis,
      
      // Use Simple mode - keep it easy for users
      suggestedMode: "simple",
      
      // Flag for backend to automatically use Image-to-Video (hidden from user)
      isContinuation: true,
      hiddenReferenceImage: lastFramePreview, // Backend will use this for visual consistency
    };
    
    // Close modal and navigate to Create Video page
    setShowContinueStoryModal(false);
    setContinuationPrompt("");
    setStoryAnalysis(null);
    
    toast.success("Opening video creator with continuation details...", { duration: 2000 });
    
    // Navigate to Create Video with pre-filled data
    navigate("/create", { 
      state: continuationData 
    });
  };

  const handleNativeShare = async (video) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: video.title,
          text: getShareText(video),
          url: getShareUrl(video),
        });
        toast.success("Shared successfully!", { duration: 3000 });
      } catch (error) {
        if (error.name !== "AbortError") {
          setShareVideo(video);
        }
      }
    } else {
      setShareVideo(video);
    }
  };

  // Handle viral score analysis for a video
  const handleCheckViralScore = async (video) => {
    if (!video.video_url && video.status !== "approved" && video.status !== "pending") {
      toast.error("Video must be ready to analyze");
      return;
    }

    setAnalyzingViralScore(true);
    setViralScoreResult(null);

    try {
      // Get the video file URL
      const token = localStorage.getItem("auth_token");
      const videoUrl = video.video_url?.startsWith('http') 
        ? video.video_url 
        : getVideoUrl(`/api/videos/${video.id}/download`, token);

      // Fetch the video as a blob
      const response = await fetch(videoUrl);
      if (!response.ok) throw new Error("Failed to fetch video");
      
      const blob = await response.blob();
      const file = new File([blob], `${video.title}.mp4`, { type: 'video/mp4' });

      // Analyze the video
      const response2 = await analyticsAPI.analyzeVideo(file);
      const result = response2.data;
      setViralScoreResult(result);
      
      // Auto-select all suggestions
      if (result.suggestions?.length > 0) {
        setSelectedSuggestions(result.suggestions.map((_, i) => i));
      }
      
      // Calculate predicted new score (estimate improvement based on number of suggestions)
      const currentScore = result.overall_score || 0;
      const maxImprovement = 100 - currentScore;
      const suggestionCount = result.suggestions?.length || 0;
      // Each implemented suggestion could improve score by ~10-15%
      const potentialImprovement = Math.min(maxImprovement, suggestionCount * 12);
      setPredictedNewScore(Math.min(100, currentScore + potentialImprovement));
      
      // Reset enhancement options
      setKeepSameVoice(true);
      setKeepSameMusic(true);
      
      toast.success("Viral score analysis complete!");
    } catch (error) {
      console.error("Viral score analysis error:", error);
      toast.error(error.message || "Failed to analyze video");
    } finally {
      setAnalyzingViralScore(false);
    }
  };

  // Handle creating enhanced video with suggestions
  const handleCreateEnhancedVideo = async (video) => {
    if (selectedSuggestions.length === 0) {
      toast.error("Please select at least one suggestion to implement");
      return;
    }

    setCreatingEnhancedVideo(true);
    
    try {
      const suggestionsToImplement = selectedSuggestions.map(i => viralScoreResult.suggestions[i]);
      
      const response = await videosAPI.createEnhanced({
        original_video_id: video.id,
        suggestions: suggestionsToImplement,
        keep_same_voice: keepSameVoice,
        keep_same_music: keepSameMusic,
        use_motion_video: useMotionVideo,
        original_analysis: {
          overall_score: viralScoreResult.overall_score,
          strengths: viralScoreResult.strengths,
          content_category: viralScoreResult.content_category
        }
      });
      
      toast.success(
        useMotionVideo 
          ? "Premium enhanced video creation started! This may take 3-5 minutes." 
          : "Enhanced video creation started! Check your library for the new video."
      );
      
      // Close modal and refresh videos
      setSelectedVideo(null);
      setViralScoreResult(null);
      fetchVideos();
      
    } catch (error) {
      console.error("Enhanced video creation error:", error);
      toast.error(error.response?.data?.detail || "Failed to create enhanced video");
    } finally {
      setCreatingEnhancedVideo(false);
    }
  };

  // Toggle suggestion selection
  const toggleSuggestion = (index) => {
    setSelectedSuggestions(prev => {
      const newSelection = prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index];
      return newSelection;
    });
  };

  // Calculate predicted score based on selected suggestions
  const predictedScore = useMemo(() => {
    if (!viralScoreResult) return 0;
    const currentScore = viralScoreResult.overall_score || 0;
    const maxImprovement = 100 - currentScore;
    const selectedCount = selectedSuggestions.length;
    const potentialImprovement = Math.min(maxImprovement, selectedCount * 12);
    return Math.min(100, currentScore + potentialImprovement);
  }, [viralScoreResult, selectedSuggestions]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-3">
        <div className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-muted-foreground">Loading your videos...</p>
      </div>
    );
  }

  // Show retry UI if data failed to load and we have no videos
  if (fetchError && videos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <Film className="w-12 h-12 text-muted-foreground" />
        <p className="text-muted-foreground">Failed to load videos</p>
        <p className="text-sm text-muted-foreground/70">This could be due to a slow connection. Please try again.</p>
        <Button 
          onClick={() => {
            setFetchError(false);
            fetchVideos();
          }} 
          className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-[#0A0A0B]"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry Loading
        </Button>
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
      data-testid="video-library-page"
    >
      {/* Header - Hidden when embedded in Media Library */}
      {!embedded && (
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
              <Film className="w-8 h-8 text-[#00F0FF]" />
              Video Library
            </h1>
            <p className="text-muted-foreground mt-2">
              View and manage all your created videos.
            </p>
          </div>

          {/* Filter and Notifications */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* Notification Permission Button */}
            {isNotificationSupported && notificationPermission !== 'granted' && (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  const granted = await requestNotificationPermission();
                  if (granted) {
                    toast.success("Notifications enabled! You'll be notified when videos are ready.", { duration: 3000 });
                  } else {
                    toast.error("Notification permission denied. You can enable it in browser settings.", { duration: 4000 });
                  }
                }}
                className="bg-[#00F0FF]/10 border-[#00F0FF]/30 hover:bg-[#00F0FF]/20 text-[#00F0FF]"
                data-testid="enable-notifications-btn"
              >
                <Bell className="w-4 h-4 mr-2" />
                Enable Notifications
              </Button>
            )}
            
            {isNotificationSupported && notificationPermission === 'granted' && (
              <div className="flex items-center gap-2 text-xs text-green-400 bg-green-400/10 px-3 py-1.5 rounded-full">
                <Bell className="w-3 h-3" />
                Notifications on
              </div>
            )}
            
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40 bg-secondary/50 border-border/50" data-testid="filter-select">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Videos</SelectItem>
                <SelectItem value="generating">Generating</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="published">Published</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Created for Clients Filter - Only for Account Managers/Admins */}
            {isAccountManager && (
              <Button
                variant={createdForClientFilter ? "default" : "outline"}
                size="sm"
                onClick={() => setCreatedForClientFilter(!createdForClientFilter)}
                className={createdForClientFilter 
                ? "bg-gradient-to-r from-cyan-500 to-blue-500 text-white border-0" 
                : "border-border/50 hover:bg-cyan-500/10 hover:text-cyan-400 hover:border-cyan-500/30"
              }
              data-testid="client-videos-filter"
            >
              <Users className="w-4 h-4 mr-2" />
              {createdForClientFilter ? "Showing Client Videos" : "Created for Clients"}
            </Button>
          )}
          
          {/* Batch Export Controls */}
          <div className="flex items-center gap-2 ml-auto">
            {isSelectMode ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectAllVideos}
                  className="border-border/50"
                >
                  Select All Completed
                </Button>
                {selectedVideoIds.size > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBatchExport}
                    disabled={isExporting}
                    className="border-[#00F0FF]/50 text-[#00F0FF] hover:bg-[#00F0FF]/10"
                    data-testid="batch-export-btn"
                  >
                    {isExporting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Package className="w-4 h-4 mr-2" />
                    )}
                    Export ({selectedVideoIds.size})
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleSelectMode}
                  className="border-border/50"
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={toggleSelectMode}
                className="border-border/50"
                data-testid="select-mode-btn"
              >
                <CheckSquare className="w-4 h-4 mr-2" />
                Select
              </Button>
            )}
          </div>
        </div>
      </div>
      )}
      
      {/* Filters when embedded */}
      {embedded && (
        <div className="flex items-center gap-3 flex-wrap px-4 sm:px-6 lg:px-8 pt-4">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 bg-secondary/50 border-border/50" data-testid="filter-select">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Videos</SelectItem>
              <SelectItem value="generating">Generating</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="published">Published</SelectItem>
            </SelectContent>
          </Select>
          
          {isAccountManager && (
            <Button
              variant={createdForClientFilter ? "default" : "outline"}
              size="sm"
              onClick={() => setCreatedForClientFilter(!createdForClientFilter)}
              className={createdForClientFilter 
                ? "bg-gradient-to-r from-cyan-500 to-blue-500 text-white border-0" 
                : "border-border/50 hover:bg-cyan-500/10 hover:text-cyan-400 hover:border-cyan-500/30"
              }
            >
              <Users className="w-4 h-4 mr-2" />
              {createdForClientFilter ? "Client Videos" : "For Clients"}
            </Button>
          )}
          
          {/* Batch Export Controls (embedded) */}
          <div className="flex items-center gap-2 ml-auto">
            {isSelectMode ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectAllVideos}
                  className="border-border/50"
                >
                  Select All
                </Button>
                {selectedVideoIds.size > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBatchExport}
                    disabled={isExporting}
                    className="border-[#00F0FF]/50 text-[#00F0FF] hover:bg-[#00F0FF]/10"
                  >
                    {isExporting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Package className="w-4 h-4 mr-2" />
                    )}
                    Export ({selectedVideoIds.size})
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleSelectMode}
                  className="border-border/50"
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={toggleSelectMode}
                className="border-border/50"
              >
                <CheckSquare className="w-4 h-4 mr-2" />
                Select
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Videos Grid */}
      {videos.length === 0 ? (
        <Card className="bg-card border-border/50">
          <CardContent className="py-16 text-center">
            <Film className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Videos Found</h3>
            <p className="text-muted-foreground">
              {createdForClientFilter
                ? "You haven't created any videos for clients yet. Go to Create Video and select a client to get started."
                : filter === "all"
                ? "Start creating videos to build your library."
                : `No ${filter} videos found.`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <motion.div
          variants={containerVariants}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {seriesView.display.map((video) => {
            const videoUnavailable = isVideoUnavailable(video);
            const stack = seriesView.stacks[video.series_id];
            return (
            <motion.div key={video.id} variants={itemVariants}>
              <Card className={`bg-card border-border/50 card-hover overflow-hidden group relative ${videoUnavailable ? 'opacity-70' : ''} ${isSelectMode && selectedVideoIds.has(video.id) ? 'ring-2 ring-[#00F0FF]' : ''}`}>
                {/* Select Checkbox - shown in select mode */}
                {isSelectMode && video.status === 'completed' && (video.cloud_video_url || video.video_url) && (
                  <div 
                    className="absolute top-2 left-2 z-20 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleVideoSelection(video.id);
                    }}
                  >
                    {selectedVideoIds.has(video.id) ? (
                      <div className="w-6 h-6 rounded bg-[#00F0FF] flex items-center justify-center">
                        <Check className="w-4 h-4 text-black" />
                      </div>
                    ) : (
                      <div className="w-6 h-6 rounded border-2 border-white/50 bg-black/30 hover:border-[#00F0FF]" />
                    )}
                  </div>
                )}
                {/* Thumbnail */}
                <div className="aspect-video bg-secondary relative overflow-hidden">
                  {video.status === "generating" || video.status === "queued" ? (
                    <div data-testid={`video-progress-${video.id}`} className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-blue-900/30 to-purple-900/30 p-4">
                      <RefreshCw className="w-10 h-10 text-blue-400 animate-spin mb-3" />
                      <p className="text-sm text-blue-400 font-medium mb-2 text-center">
                        {videoProgress[video.id]?.message || video.progress || "Generating video..."}
                      </p>
                      <div className="w-full max-w-[200px]">
                        <Progress 
                          value={videoProgress[video.id]?.progress || video.progress_percent || 5} 
                          className="h-2 bg-blue-900/50"
                        />
                        <p className="text-xs text-blue-300 text-center mt-1">
                          {videoProgress[video.id]?.progress || video.progress_percent || 5}% complete
                        </p>
                      </div>
                    </div>
                  ) : videoUnavailable ? (
                    // Unavailable video - show placeholder with warning
                    <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800 p-4">
                      <AlertTriangle className="w-10 h-10 text-amber-500/70 mb-2" />
                      <p className="text-xs text-amber-500/80 text-center font-medium">Video Unavailable</p>
                      <p className="text-xs text-muted-foreground text-center mt-1">Legacy file not found</p>
                    </div>
                  ) : (video.thumbnail_url || video.cloud_thumbnail_url) ? (
                    <img
                      src={getThumbnailUrl(video)}
                      alt={video.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                      onError={(e) => {
                        // If thumbnail fails to load, show placeholder
                        e.target.style.display = 'none';
                        e.target.nextSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  {/* Fallback: Use video element to show first frame as thumbnail */}
                  {!videoUnavailable && (video.video_url || video.cloud_video_url) && !(video.thumbnail_url || video.cloud_thumbnail_url) ? (
                    <video
                      src={getVideoUrl(`/api/videos/${video.id}/stream`, localStorage.getItem("auth_token"))}
                      className="w-full h-full object-cover"
                      muted
                      preload="metadata"
                      onLoadedMetadata={(e) => {
                        // Seek to 1 second to get a better frame
                        e.target.currentTime = 1;
                      }}
                    />
                  ) : !videoUnavailable && !(video.thumbnail_url || video.cloud_thumbnail_url) && !(video.video_url || video.cloud_video_url) ? (
                    <div className="w-full h-full flex items-center justify-center bg-gray-800">
                      <Film className="w-12 h-12 text-muted-foreground" />
                    </div>
                  ) : null}
                  {/* Hidden placeholder for when image fails to load */}
                  <div className="hidden w-full h-full flex items-center justify-center bg-gray-800">
                    <Film className="w-12 h-12 text-muted-foreground" />
                  </div>
                  {video.episode_number && !video.is_series_merge && (
                    <Badge data-testid={`episode-badge-${video.id}`} className="absolute top-2 left-2 z-10 bg-[#A855F7] text-white border-0">Ep {video.episode_number}</Badge>
                  )}
                  {video.is_series_merge && (
                    <Badge data-testid={`full-series-badge-${video.id}`} className="absolute top-2 left-2 z-10 bg-[#00F0FF] text-black border-0">Full Series</Badge>
                  )}
                  {stack && !video.is_series_merge && stack.episodes.length > 1 && (
                    <button type="button" onClick={(e) => { e.stopPropagation(); setOpenSeriesId(video.series_id); }}
                      data-testid={`series-stack-btn-${video.series_id}`}
                      className="absolute bottom-2 left-2 z-20 flex items-center gap-1.5 rounded-full bg-black/75 px-3 py-1 text-xs font-medium text-white ring-1 ring-[#A855F7]/60 hover:bg-[#A855F7]/80 transition-colors">
                      <Film className="w-3.5 h-3.5" /> Series · {stack.episodes.length} episodes
                    </button>
                  )}
                  {/* Overlay Actions */}
                  {video.status !== "generating" && (
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 text-white"
                        onClick={() => setSelectedVideo(video)}
                        data-testid={`view-video-btn-${video.id}`}
                      >
                        <Eye className="w-5 h-5" />
                      </Button>
                      {!videoUnavailable && (video.video_url || video.cloud_video_url || video.status === "completed") && (
                        <>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="w-10 h-10 rounded-full bg-[#00F0FF]/20 hover:bg-[#00F0FF]/30 text-[#00F0FF]"
                            onClick={() => handleDownload(video)}
                            data-testid={`download-video-btn-${video.id}`}
                          >
                            <Download className="w-5 h-5" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="w-10 h-10 rounded-full bg-[#FF0055]/20 hover:bg-[#FF0055]/30 text-[#FF0055]"
                            onClick={() => handleNativeShare(video)}
                            data-testid={`share-video-btn-${video.id}`}
                          >
                            <Share2 className="w-5 h-5" />
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                  {/* Status Badge - show 'Unavailable' for legacy videos */}
                  {videoUnavailable ? (
                    <Badge className="absolute top-3 right-3 bg-amber-600/80 text-white">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Unavailable
                    </Badge>
                  ) : (
                  <Badge
                    className={`absolute top-3 right-3 ${getStatusColor(video.status)}`}
                  >
                    {video.status === "generating" && (
                      <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                    )}
                    {video.status}
                  </Badge>
                  )}
                </div>

                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-white truncate">{video.title}</h3>
                      <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(video.created_at)}
                      </p>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setSelectedVideo(video)}>
                          <Eye className="w-4 h-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        {!videoUnavailable && video.video_url && video.status !== "generating" && (
                          <>
                            <DropdownMenuItem onClick={() => handleNativeShare(video)}>
                              <Share2 className="w-4 h-4 mr-2" />
                              Share Video
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setCaptionsModalVideo(video)}>
                              <Type className="w-4 h-4 mr-2" />
                              {video.caption_style ? "Change Captions" : "Add Captions"}
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => navigate("/video-studio", { state: { editVideo: video } })}
                              className="text-[#A855F7]"
                              data-testid="edit-video-studio-btn"
                            >
                              <Wand2 className="w-4 h-4 mr-2" />
                              Edit with Video Studio
                            </DropdownMenuItem>
                            {(video.status === "completed" || video.status === "pending") && video.video_url && (
                              <DropdownMenuItem 
                                onClick={() => {
                                  setSelectedVideo(video);
                                  setShowContinueStoryModal(true);
                                }}
                                className="text-[#00F0FF]"
                                data-testid="continue-story-btn"
                              >
                                <Film className="w-4 h-4 mr-2" />
                                Continue Story
                              </DropdownMenuItem>
                            )}
                            {video.status === "completed" && (
                              <DropdownMenuItem 
                                onClick={() => handleOpenSaveTemplate(video)}
                                className="text-[#00FF94]"
                                data-testid="save-as-template-btn"
                              >
                                <Bookmark className="w-4 h-4 mr-2" />
                                Save as Template
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handlePostToTikTok(video)}>
                              <TikTokLogo className="w-4 h-4 mr-2" />
                              Post to TikTok
                            </DropdownMenuItem>
                          </>
                        )}
                        {/* Regenerate option for unavailable videos */}
                        {videoUnavailable && (
                          <DropdownMenuItem
                            onClick={() => handleRetryVideo(video.id)}
                            disabled={retrying === video.id}
                            className="text-[#00F0FF]"
                          >
                            {retrying === video.id ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <RefreshCw className="w-4 h-4 mr-2" />
                            )}
                            Regenerate Video
                          </DropdownMenuItem>
                        )}
                        {(video.status === "failed" || video.status === "error" || video.status === "generating") && (
                          <>
                            <DropdownMenuItem
                              onClick={() => handleRetryVideo(video.id)}
                              disabled={retrying === video.id}
                              className="text-blue-400"
                            >
                              {retrying === video.id ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <RefreshCw className="w-4 h-4 mr-2" />
                              )}
                              {video.status === "generating" ? "Restart Generation" : "Retry Generation"}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleRetryNoVoice(video.id)}
                              disabled={retrying === video.id}
                              className="text-yellow-400"
                            >
                              {retrying === video.id ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <RefreshCw className="w-4 h-4 mr-2" />
                              )}
                              Retry (No Voiceover)
                            </DropdownMenuItem>
                          </>
                        )}
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() => handleDelete(video.id)}
                          disabled={deleting === video.id}
                        >
                          {deleting === video.id ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4 mr-2" />
                          )}
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {/* Hashtags */}
                  {video.hashtags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {video.hashtags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="text-xs px-2 py-0.5 rounded-full bg-secondary text-muted-foreground"
                        >
                          {tag}
                        </span>
                      ))}
                      {video.hashtags.length > 3 && (
                        <span className="text-xs text-muted-foreground">
                          +{video.hashtags.length - 3}
                        </span>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          );
          })}
        </motion.div>
      )}

      {/* Video Detail Modal */}
      <Dialog open={!!selectedVideo} onOpenChange={() => {
        setSelectedVideo(null);
        setViralScoreResult(null);
        setSelectedSuggestions([]);
        setPredictedNewScore(null);
      }}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto bg-card border-border/50 p-4 md:p-6">
          <DialogHeader>
            <DialogTitle className="text-lg md:text-xl font-semibold text-white pr-8">
              {selectedVideo?.title}
            </DialogTitle>
          </DialogHeader>
          {selectedVideo && (
            <div className="space-y-6">
              {/* Video Player or Thumbnail */}
              <div className="aspect-video bg-secondary rounded-xl overflow-hidden relative">
                {selectedVideo.status === "generating" ? (
                  <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-blue-900/30 to-purple-900/30">
                    <RefreshCw className="w-16 h-16 text-blue-400 animate-spin mb-4" />
                    <p className="text-lg text-blue-400 font-medium">Generating your video...</p>
                    <p className="text-sm text-muted-foreground mt-2">This may take a minute</p>
                  </div>
                ) : isVideoUnavailable(selectedVideo) ? (
                  // Unavailable video - show user-friendly message with regenerate option
                  <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-800/50 to-gray-900 p-6">
                    <RefreshCw className="w-16 h-16 text-muted-foreground/50 mb-4" />
                    <p className="text-lg text-foreground font-medium text-center">Video Processing Required</p>
                    <p className="text-sm text-muted-foreground mt-2 text-center max-w-md">
                      This video needs to be regenerated. Click below to create a fresh copy with the same settings.
                    </p>
                    <Button
                      onClick={() => handleRetryVideo(selectedVideo.id)}
                      disabled={retrying === selectedVideo.id}
                      className="mt-4 bg-gradient-to-r from-[#00F0FF] to-[#00F0FF]/80 hover:opacity-90"
                      data-testid="regenerate-unavailable-video-btn"
                    >
                      {retrying === selectedVideo.id ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      Regenerate Video
                    </Button>
                  </div>
                ) : (selectedVideo.video_url || selectedVideo.cloud_video_url || selectedVideo.status === "completed") ? (
                  <video
                    key={`video-${selectedVideo.id}`}
                    src={
                      // Always use backend stream endpoint - it properly proxies from S3 with authentication
                      // Direct S3 URLs return 403 since bucket is not public
                      getVideoUrl(`/api/videos/${selectedVideo.id}/stream`, localStorage.getItem("auth_token"))
                    }
                    controls
                    preload="metadata"
                    playsInline
                    className="w-full h-full object-contain bg-black"
                    poster={getThumbnailUrl(selectedVideo)}
                    onError={(e) => {
                      console.error("Video playback error:", e);
                      const video = e.target;
                      if (video.error) {
                        console.log("Video error code:", video.error.code);
                        // Error code 4 = MEDIA_ERR_SRC_NOT_SUPPORTED (often means 404)
                        // Log the error but show a user-friendly message
                        if (video.error.code === 4) {
                          console.error("[VideoLibrary] Video file not found - may need regeneration:", selectedVideo?.id);
                          toast.error("Unable to play this video. Try regenerating it using the button below.", {
                            duration: 5000
                          });
                        }
                      }
                    }}
                  >
                    {/* Subtitles track */}
                    {selectedVideo.srt_url && (
                      <track
                        kind="subtitles"
                        label="English"
                        srcLang="en"
                        src={getAssetUrl(selectedVideo.srt_url)}
                        default
                      />
                    )}
                  </video>
                ) : (selectedVideo.thumbnail_url || selectedVideo.cloud_thumbnail_url) ? (
                  <img
                    src={getThumbnailUrl(selectedVideo)}
                    alt={selectedVideo.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Film className="w-16 h-16 text-muted-foreground" />
                  </div>
                )}
              </div>

              {/* AI Image Fallback Notice */}
              {selectedVideo.fallback_mode === "ai_images" && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <p className="text-amber-300 text-sm">
                    {selectedVideo.fallback_message || "Video created using AI-generated images (stock footage was unavailable)"}
                  </p>
                </div>
              )}

              {/* Download Button - Only show for available videos */}
              {!isVideoUnavailable(selectedVideo) && (selectedVideo.video_url || selectedVideo.cloud_video_url || selectedVideo.status === "completed" || selectedVideo.status === "pending") && selectedVideo.status !== "generating" && (
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                    <Button
                      onClick={() => handleDownload(selectedVideo)}
                      className="flex-1 bg-gradient-to-r from-[#00F0FF] to-[#FF006F] hover:opacity-90 text-sm md:text-base"
                      data-testid="download-video-modal-btn"
                    >
                      <Download className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2" />
                      Download Video
                    </Button>
                    <Button
                      onClick={() => handlePostToTikTok(selectedVideo)}
                      disabled={postingToTikTok === selectedVideo.id}
                      className="flex-1 bg-gradient-to-r from-[#FF0050] to-[#00F2EA] hover:opacity-90 text-white font-bold text-sm md:text-base"
                      data-testid="post-to-tiktok-modal-btn"
                    >
                      {postingToTikTok === selectedVideo.id ? (
                        <Loader2 className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2 animate-spin" />
                      ) : (
                        <TikTokLogo className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2" />
                      )}
                      <span className="hidden sm:inline">Post to TikTok</span>
                      <span className="sm:hidden">TikTok</span>
                    </Button>
                  </div>
                  {/* Share/Save Button - Opens download dialog with all options */}
                  <Button
                    onClick={() => handleDownload(selectedVideo)}
                    variant="outline"
                    className="w-full border-[#FF0055]/50 text-[#FF0055] hover:bg-[#FF0055]/10"
                    data-testid="share-video-modal-btn"
                  >
                    <Share2 className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                    Share & Save Options
                  </Button>
                  {/* Download Subtitles */}
                  {selectedVideo.srt_url && (
                    <a 
                      href={getVideoUrl(`/api/videos/${selectedVideo.id}/subtitles/download`, localStorage.getItem("auth_token"))}
                      download={`${selectedVideo.title}_subtitles.srt`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 text-xs text-[#00F0FF] hover:text-[#00F0FF]/80 py-2"
                      data-testid="download-subtitles-btn"
                    >
                      <Captions className="w-4 h-4" />
                      Download Subtitles (.srt)
                    </a>
                  )}
                </div>
              )}

              {/* Info */}
              <div className="flex items-center gap-4 flex-wrap">
                <Badge className={getStatusColor(selectedVideo.status)}>
                  {selectedVideo.status === "generating" && (
                    <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                  )}
                  {selectedVideo.status}
                </Badge>
                <span className="text-sm text-muted-foreground flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDate(selectedVideo.created_at)}
                </span>
                {selectedVideo.credits_refunded && selectedVideo.credits_refunded_amount > 0 && (
                  <Badge data-testid="credits-refunded-badge" className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                    {selectedVideo.credits_refunded_amount} credit{selectedVideo.credits_refunded_amount > 1 ? "s" : ""} refunded
                  </Badge>
                )}
                {(selectedVideo.status === "failed" || selectedVideo.status === "error" || selectedVideo.status === "generating") && (
                  <>
                    <Button
                      onClick={() => handleRetryVideo(selectedVideo.id)}
                      disabled={retrying === selectedVideo.id}
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="retry-video-btn"
                    >
                      {retrying === selectedVideo.id ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      {selectedVideo.status === "generating" ? "Restart" : "Retry"}
                    </Button>
                    <Button
                      onClick={() => handleRetryNoVoice(selectedVideo.id)}
                      disabled={retrying === selectedVideo.id}
                      size="sm"
                      variant="outline"
                      className="border-yellow-500 text-yellow-400 hover:bg-yellow-500/10"
                      data-testid="retry-no-voice-btn"
                    >
                      {retrying === selectedVideo.id ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      No Voice
                    </Button>
                  </>
                )}
              </div>

              {/* Stuck at 60% Help Message */}
              {selectedVideo.status === "generating" && selectedVideo.progress_percent >= 55 && selectedVideo.progress_percent <= 65 && (
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                  <p className="text-sm text-yellow-400">
                    <strong>Tip:</strong> If your video is stuck at 60%, try &quot;No Voice&quot; option - this skips voiceover generation which can timeout in some cases.
                  </p>
                </div>
              )}

              {/* Error Message */}
              {selectedVideo.error && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <p className="text-sm text-red-400">
                    <strong>Error:</strong> {selectedVideo.error}
                  </p>
                </div>
              )}

              {/* Content Policy Violation Notice */}
              {selectedVideo.error_type === "content_policy" && selectedVideo.content_policy_analysis && (
                <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm text-orange-300 font-medium">Content Policy Violation</p>
                      <p className="text-sm text-orange-300/80 mt-1">
                        Your prompt was flagged by AI safety filters. Click below to see what triggered the flag and get suggestions for rephrasing.
                      </p>
                      <Button
                        onClick={() => {
                          setContentPolicyAnalysis(selectedVideo.content_policy_analysis);
                          setContentPolicyVideoId(selectedVideo.id);
                          setContentPolicyModalOpen(true);
                        }}
                        size="sm"
                        className="mt-3 bg-orange-500/20 hover:bg-orange-500/30 text-orange-300 border border-orange-500/30"
                        data-testid="view-content-policy-btn"
                      >
                        <AlertTriangle className="w-4 h-4 mr-2" />
                        View Details & Suggestions
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Script */}
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Script
                </h4>
                <ScrollArea className="h-48 p-4 bg-secondary/30 rounded-xl">
                  <pre className="text-sm text-white/90 whitespace-pre-wrap font-mono">
                    {selectedVideo.script}
                  </pre>
                </ScrollArea>
              </div>

              {/* Hashtags */}
              {selectedVideo.hashtags?.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-muted-foreground">Hashtags</h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs text-muted-foreground hover:text-white"
                      onClick={() => {
                        // Clean up script - remove section headers and formatting
                        let cleanScript = selectedVideo.script || '';
                        cleanScript = cleanScript
                          .replace(/\*\*[A-Z\s]+:?\*\*/g, '') // Remove **HOOK:** etc
                          .replace(/\*\*/g, '')  // Remove remaining **
                          .replace(/^\s*[-•]\s*/gm, '') // Remove bullet points
                          .replace(/\n{3,}/g, '\n\n') // Max 2 newlines
                          .trim();
                        
                        // Format hashtags - ensure they have #
                        const hashtags = selectedVideo.hashtags
                          .map(tag => tag.startsWith('#') ? tag : `#${tag}`)
                          .join(' ');
                        
                        // Build the caption for TikTok/Instagram
                        const caption = [
                          selectedVideo.title,
                          '',
                          cleanScript,
                          '',
                          hashtags
                        ].filter(line => line !== undefined).join('\n').trim();
                        
                        navigator.clipboard.writeText(caption);
                        setCopiedHashtags(true);
                        toast.success("Caption copied! Ready to paste on TikTok/Instagram");
                        setTimeout(() => setCopiedHashtags(false), 2000);
                      }}
                      data-testid="copy-caption-btn"
                    >
                      {copiedHashtags ? (
                        <>
                          <Check className="w-3 h-3 mr-1 text-green-500" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3 mr-1" />
                          Copy Caption
                        </>
                      )}
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedVideo.hashtags.map((tag) => (
                      <Badge key={tag} variant="outline" className="badge-trending gap-1">
                        <Hash className="w-3 h-3" />
                        {tag.replace("#", "")}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Check Viral Score Button */}
              {(selectedVideo.status === "approved" || selectedVideo.status === "pending" || selectedVideo.video_url) && (
                <div className="pt-4 border-t border-border/30">
                  <Button
                    onClick={() => handleCheckViralScore(selectedVideo)}
                    disabled={analyzingViralScore}
                    className="w-full bg-gradient-to-r from-[#FF0055] to-[#FF0055]/80 hover:from-[#FF0055]/90 hover:to-[#FF0055]/70 text-white font-medium"
                    data-testid="check-viral-score-btn"
                  >
                    {analyzingViralScore ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing Video...
                      </>
                    ) : (
                      <>
                        <TrendingUp className="w-4 h-4 mr-2" />
                        Check Viral Score
                      </>
                    )}
                  </Button>

                  {/* Viral Score Results */}
                  {viralScoreResult && (
                    <div className="mt-4 p-4 bg-gradient-to-br from-[#FF0055]/10 to-[#00F0FF]/10 rounded-xl border border-border/30 space-y-4">
                      {/* Overall Score */}
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-lg font-semibold text-white">Viral Score</h4>
                          <p className="text-sm text-muted-foreground">{viralScoreResult.content_category}</p>
                        </div>
                        <div className={`text-3xl font-bold ${
                          viralScoreResult.overall_score >= 80 ? 'text-green-500' :
                          viralScoreResult.overall_score >= 60 ? 'text-yellow-500' :
                          viralScoreResult.overall_score >= 40 ? 'text-orange-500' : 'text-red-500'
                        }`}>
                          {viralScoreResult.overall_score}%
                        </div>
                      </div>

                      {/* Score Breakdown */}
                      <div className="grid grid-cols-5 gap-2">
                        {[
                          { label: "Visual", value: viralScoreResult.visual_appeal },
                          { label: "Hook", value: viralScoreResult.hook_strength },
                          { label: "Engage", value: viralScoreResult.engagement_potential },
                          { label: "Trend", value: viralScoreResult.trend_alignment },
                          { label: "Pacing", value: viralScoreResult.pacing_score },
                        ].map((item) => (
                          <div key={item.label} className="text-center">
                            <div className={`text-sm font-semibold ${
                              item.value >= 70 ? 'text-green-400' :
                              item.value >= 50 ? 'text-yellow-400' : 'text-red-400'
                            }`}>
                              {item.value}
                            </div>
                            <div className="text-xs text-muted-foreground">{item.label}</div>
                          </div>
                        ))}
                      </div>

                      {/* Analysis Summary */}
                      <p className="text-sm text-muted-foreground">{viralScoreResult.analysis_summary}</p>

                      {/* Predicted Reach */}
                      <div className="flex items-center gap-2 text-sm">
                        <TrendingUp className="w-4 h-4 text-[#00F0FF]" />
                        <span className="text-[#00F0FF] font-medium">{viralScoreResult.predicted_reach}</span>
                      </div>

                      {/* Suggestions with Checkboxes */}
                      {viralScoreResult.suggestions?.length > 0 && (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <h5 className="text-sm font-medium text-white">Implement Suggestions</h5>
                            <span className="text-xs text-muted-foreground">
                              {selectedSuggestions.length} of {viralScoreResult.suggestions.length} selected
                            </span>
                          </div>
                          
                          <div className="space-y-2 max-h-40 overflow-y-auto pr-2">
                            {viralScoreResult.suggestions.map((suggestion, i) => (
                              <label 
                                key={i} 
                                className={`flex items-start gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                                  selectedSuggestions.includes(i) 
                                    ? 'bg-[#00F0FF]/10 border border-[#00F0FF]/30' 
                                    : 'bg-secondary/30 border border-transparent hover:bg-secondary/50'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedSuggestions.includes(i)}
                                  onChange={() => toggleSuggestion(i)}
                                  className="mt-1 w-4 h-4 rounded border-border accent-[#00F0FF]"
                                />
                                <span className="text-xs text-muted-foreground flex-1">{suggestion}</span>
                              </label>
                            ))}
                          </div>

                          {/* Video Settings Options */}
                          <div className="pt-3 border-t border-border/30 space-y-2">
                            <h5 className="text-sm font-medium text-white mb-2">Video Settings</h5>
                            <label className="flex items-center gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={keepSameVoice}
                                onChange={(e) => setKeepSameVoice(e.target.checked)}
                                className="w-4 h-4 rounded border-border accent-[#00F0FF]"
                              />
                              <span className="text-xs text-muted-foreground">Keep same voice settings</span>
                            </label>
                            <label className="flex items-center gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={keepSameMusic}
                                onChange={(e) => setKeepSameMusic(e.target.checked)}
                                className="w-4 h-4 rounded border-border accent-[#00F0FF]"
                              />
                              <span className="text-xs text-muted-foreground">Keep same background music</span>
                            </label>
                            <label className="flex items-center gap-3 cursor-pointer mt-2 p-2 rounded-lg bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20">
                              <input
                                type="checkbox"
                                checked={useMotionVideo}
                                onChange={(e) => setUseMotionVideo(e.target.checked)}
                                className="w-4 h-4 rounded border-border accent-purple-500"
                              />
                              <div>
                                <span className="text-xs text-white font-medium">Use Motion Video (Premium)</span>
                                <p className="text-xs text-muted-foreground">Generate with AI motion instead of still images</p>
                              </div>
                            </label>
                          </div>

                          {/* Predicted New Score */}
                          <div className="pt-3 border-t border-border/30">
                            <div className="flex items-center justify-between p-3 bg-gradient-to-r from-green-500/10 to-[#00F0FF]/10 rounded-lg">
                              <div>
                                <p className="text-xs text-muted-foreground">Predicted New Score</p>
                                <p className="text-xs text-muted-foreground/70">with {selectedSuggestions.length} suggestion{selectedSuggestions.length !== 1 ? 's' : ''}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-lg text-muted-foreground">{viralScoreResult.overall_score}%</span>
                                <span className="text-[#00F0FF]">→</span>
                                <span className={`text-2xl font-bold ${
                                  predictedScore >= 80 ? 'text-green-500' :
                                  predictedScore >= 60 ? 'text-yellow-500' : 'text-orange-500'
                                }`}>
                                  {predictedScore}%
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Create Enhanced Video Button */}
                          <Button
                            onClick={() => handleCreateEnhancedVideo(selectedVideo)}
                            disabled={creatingEnhancedVideo || selectedSuggestions.length === 0}
                            className="w-full bg-gradient-to-r from-green-500 to-[#00F0FF] hover:from-green-600 hover:to-[#00F0FF]/90 text-black font-semibold"
                            data-testid="create-enhanced-video-btn"
                          >
                            {creatingEnhancedVideo ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Creating Enhanced Video...
                              </>
                            ) : (
                              <>
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Create Enhanced Video
                              </>
                            )}
                          </Button>
                          <p className="text-xs text-center text-muted-foreground">
                            A new video will be created with the selected improvements
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Share Modal */}
      <Dialog open={!!shareVideo} onOpenChange={() => setShareVideo(null)}>
        <DialogContent className="max-w-md w-[95vw] bg-card border-border/50">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-white flex items-center gap-2">
              <Share2 className="w-5 h-5 text-[#FF0055]" />
              Share Video
            </DialogTitle>
          </DialogHeader>
          {shareVideo && (
            <div className="space-y-6">
              {/* Video Info */}
              <div className="flex items-center gap-3 p-3 bg-secondary/30 rounded-xl">
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-secondary flex-shrink-0">
                  {shareVideo.thumbnail_url ? (
                    <img
                      src={getThumbnailUrl(shareVideo)}
                      alt={shareVideo.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Film className="w-6 h-6 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-white truncate">{shareVideo.title}</h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    Created with iGoViral Content Creator
                  </p>
                </div>
              </div>

              {/* Copy Link */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">Share Link</label>
                <div className="flex gap-2">
                  <div className="flex-1 px-3 py-2 bg-secondary/50 rounded-lg text-sm text-white/80 truncate border border-border/50">
                    {getShareUrl(shareVideo)}
                  </div>
                  <Button
                    onClick={() => handleCopyLink(shareVideo)}
                    variant="outline"
                    size="icon"
                    className={`border-border/50 ${copiedLink ? 'bg-green-500/20 text-green-400 border-green-500/50' : ''}`}
                    data-testid="copy-share-link-btn"
                  >
                    {copiedLink ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {/* Social Media Grid */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">Share on Social Media</label>
                <div className="grid grid-cols-4 gap-3">
                  {/* Twitter/X */}
                  <a
                    href={shareLinks(shareVideo).twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center gap-2 p-3 rounded-xl bg-secondary/30 hover:bg-secondary/50 transition-colors group"
                    data-testid="share-twitter-btn"
                  >
                    <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center group-hover:scale-110 transition-transform">
                      <TwitterIcon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-xs text-muted-foreground">X</span>
                  </a>

                  {/* Facebook */}
                  <a
                    href={shareLinks(shareVideo).facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center gap-2 p-3 rounded-xl bg-secondary/30 hover:bg-secondary/50 transition-colors group"
                    data-testid="share-facebook-btn"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#1877F2] flex items-center justify-center group-hover:scale-110 transition-transform">
                      <FacebookIcon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-xs text-muted-foreground">Facebook</span>
                  </a>

                  {/* WhatsApp */}
                  <a
                    href={shareLinks(shareVideo).whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center gap-2 p-3 rounded-xl bg-secondary/30 hover:bg-secondary/50 transition-colors group"
                    data-testid="share-whatsapp-btn"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center group-hover:scale-110 transition-transform">
                      <WhatsAppIcon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-xs text-muted-foreground">WhatsApp</span>
                  </a>

                  {/* LinkedIn */}
                  <a
                    href={shareLinks(shareVideo).linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center gap-2 p-3 rounded-xl bg-secondary/30 hover:bg-secondary/50 transition-colors group"
                    data-testid="share-linkedin-btn"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#0A66C2] flex items-center justify-center group-hover:scale-110 transition-transform">
                      <LinkedInIcon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-xs text-muted-foreground">LinkedIn</span>
                  </a>

                  {/* Telegram */}
                  <a
                    href={shareLinks(shareVideo).telegram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center gap-2 p-3 rounded-xl bg-secondary/30 hover:bg-secondary/50 transition-colors group"
                    data-testid="share-telegram-btn"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#0088CC] flex items-center justify-center group-hover:scale-110 transition-transform">
                      <TelegramIcon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-xs text-muted-foreground">Telegram</span>
                  </a>

                  {/* Reddit */}
                  <a
                    href={shareLinks(shareVideo).reddit}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center gap-2 p-3 rounded-xl bg-secondary/30 hover:bg-secondary/50 transition-colors group"
                    data-testid="share-reddit-btn"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#FF4500] flex items-center justify-center group-hover:scale-110 transition-transform">
                      <RedditIcon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-xs text-muted-foreground">Reddit</span>
                  </a>

                  {/* Instagram (Copy for Stories) */}
                  <a
                    href="/cross-post"
                    className="flex flex-col items-center gap-2 p-3 rounded-xl bg-secondary/30 hover:bg-secondary/50 transition-colors group"
                    data-testid="share-instagram-btn"
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#F77737] flex items-center justify-center group-hover:scale-110 transition-transform">
                      <InstagramIcon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-xs text-muted-foreground">Instagram</span>
                  </a>

                  {/* TikTok */}
                  <button
                    onClick={() => {
                      setShareVideo(null);
                      handlePostToTikTok(shareVideo);
                    }}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl bg-secondary/30 hover:bg-secondary/50 transition-colors group"
                    data-testid="share-tiktok-btn"
                  >
                    <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center group-hover:scale-110 transition-transform">
                      <TikTokLogo className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-xs text-muted-foreground">TikTok</span>
                  </button>
                </div>
              </div>

              {/* Domain Attribution */}
              <div className="text-center pt-4 border-t border-border/30">
                <p className="text-xs text-muted-foreground">
                  Powered by{" "}
                  <a 
                    href="https://igoviralcreator.io" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#00F0FF] hover:underline"
                  >
                    igoviralcreator.io
                  </a>
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Post to TikTok Dialog */}
      <Dialog open={!!postToTiktokDialog} onOpenChange={() => setPostToTiktokDialog(null)}>
        <DialogContent className="max-w-md w-[95vw] bg-card border-border/50">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-white flex items-center gap-2">
              <TikTokLogo className="w-5 h-5" />
              Post to TikTok
            </DialogTitle>
          </DialogHeader>
          {postToTiktokDialog && (
            <div className="space-y-6">
              {/* Video Info */}
              <div className="flex items-center gap-3 p-3 bg-secondary/30 rounded-xl">
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-secondary flex-shrink-0">
                  {postToTiktokDialog.thumbnail_url ? (
                    <img
                      src={getThumbnailUrl(postToTiktokDialog)}
                      alt={postToTiktokDialog.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Film className="w-6 h-6 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-white truncate">{postToTiktokDialog.title}</h4>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {postToTiktokDialog.hashtags?.slice(0, 3).map((tag) => (
                      <span key={tag} className="text-xs text-[#00F0FF]">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Account Selector */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">Select TikTok Account</label>
                {tiktokAccounts.length === 0 ? (
                  <div className="p-4 border border-border/50 rounded-xl text-center">
                    <p className="text-muted-foreground text-sm mb-3">No TikTok accounts connected</p>
                    <Button
                      onClick={() => {
                        setPostToTiktokDialog(null);
                        window.location.href = "/tiktok";
                      }}
                      className="bg-gradient-to-r from-[#FF0050] to-[#00F2EA] hover:opacity-90 text-white"
                      size="sm"
                    >
                      Connect Account
                    </Button>
                  </div>
                ) : (
                  <div className="grid gap-2">
                    {tiktokAccounts.map((account) => (
                      <div
                        key={account.id}
                        onClick={() => setSelectedTiktokAccount(account)}
                        className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                          selectedTiktokAccount?.id === account.id
                            ? "border-[#00F0FF] bg-[#00F0FF]/10"
                            : "border-border/50 bg-secondary/20 hover:border-border"
                        }`}
                        data-testid={`select-account-${account.tiktok_username}`}
                      >
                        <img
                          src={account.tiktok_avatar}
                          alt={account.tiktok_display_name}
                          className="w-10 h-10 rounded-full border border-white/20"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-white truncate">{account.tiktok_display_name}</p>
                            {account.is_default && (
                              <Badge className="bg-[#00F0FF]/20 text-[#00F0FF] border-none text-[10px] px-1.5 py-0">
                                Default
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">@{account.tiktok_username}</p>
                        </div>
                        {selectedTiktokAccount?.id === account.id && (
                          <div className="w-5 h-5 rounded-full bg-[#00F0FF] flex items-center justify-center">
                            <Check className="w-3 h-3 text-[#0A0A0B]" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Demo Notice */}
              <div className="p-3 bg-[#FF0055]/10 border border-[#FF0055]/30 rounded-lg">
                <p className="text-xs text-muted-foreground">
                  <span className="text-[#FF0055] font-medium">Demo Mode:</span> This simulates posting. 
                  Real TikTok API integration requires TikTok Developer credentials.
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 border-border/50"
                  onClick={() => setPostToTiktokDialog(null)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-gradient-to-r from-[#FF0050] to-[#00F2EA] hover:opacity-90 text-white"
                  onClick={confirmPostToTikTok}
                  disabled={!selectedTiktokAccount || postingToTikTok}
                  data-testid="confirm-post-to-tiktok-btn"
                >
                  {postingToTikTok ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Post Now
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Video Download Dialog */}
      <VideoDownloadDialog
        open={!!downloadDialogVideo}
        onOpenChange={(open) => !open && setDownloadDialogVideo(null)}
        video={downloadDialogVideo}
        onDownloadComplete={() => setDownloadDialogVideo(null)}
      />

      {/* Apply Captions Modal */}
      <ApplyCaptionsModal
        open={!!captionsModalVideo}
        onClose={() => setCaptionsModalVideo(null)}
        videoId={captionsModalVideo?.id}
        currentStyle={captionsModalVideo?.caption_style}
        onCaptionsApplied={(result) => {
          // Update the video in the list with the new caption style
          setVideos(prev => prev.map(v => 
            v.id === captionsModalVideo?.id 
              ? { ...v, caption_style: result.caption_style }
              : v
          ));
        }}
      />

      {/* TikTok OAuth Share Dialog */}
      <TikTokShareDialog
        video={tiktokShareVideo}
        open={!!tiktokShareVideo}
        onOpenChange={(open) => !open && setTiktokShareVideo(null)}
      />
      
      {/* Content Policy Modal */}
      <ContentPolicyModal
        isOpen={contentPolicyModalOpen}
        onClose={() => {
          setContentPolicyModalOpen(false);
          setContentPolicyAnalysis(null);
          setContentPolicyVideoId(null);
        }}
        analysis={contentPolicyAnalysis}
        onRetryWithSuggestion={(newPrompt) => {
          // Close modal and navigate to create page with the new prompt
          setContentPolicyModalOpen(false);
          navigate("/create", { 
            state: { 
              prefillScript: newPrompt,
              fromContentPolicy: true 
            } 
          });
        }}
        onEditPrompt={() => {
          // Close modal and navigate to create page for manual editing
          setContentPolicyModalOpen(false);
          navigate("/create");
        }}
      />

      {/* Story Continuation Modal */}
      <Dialog open={showContinueStoryModal} onOpenChange={(open) => {
        if (!open) {
          setShowContinueStoryModal(false);
          setStoryAnalysis(null);
          setContinuationPrompt("");
          setLastFramePreview(null);
        }
      }}>
        <DialogContent className="max-w-2xl bg-background border-border/50 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Film className="w-5 h-5 text-[#00F0FF]" />
              Continue Story
            </DialogTitle>
            <DialogDescription>
              Create a sequel episode that continues from "{selectedVideo?.title}"
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Step 1: Analyze */}
            {!storyAnalysis && (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">
                  First, let AI analyze your video to understand the characters, setting, and story context.
                </p>
                <Button
                  data-testid="analyze-video-btn"
                  onClick={() => selectedVideo && analyzeVideoForContinuation(selectedVideo)}
                  disabled={analyzingStory}
                  className="bg-gradient-to-r from-[#00F0FF] to-[#A855F7]"
                >
                  {analyzingStory ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing Video...</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-2" /> Analyze Video</>
                  )}
                </Button>
              </div>
            )}

            {/* Step 2: Show Analysis & Get Continuation */}
            {storyAnalysis && (
              <>
                {/* Last Frame Preview */}
                {lastFramePreview && (
                  <EndingFramePicker frames={endingFrames} selected={lastFramePreview}
                    onSelect={setLastFramePreview} episode={seriesInfo?.next} />
                )}

                {/* Characters Detected */}
                {storyAnalysis.characters?.length > 0 && (
                  <div className="bg-secondary/30 rounded-lg p-3">
                    <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                      <Users className="w-4 h-4 text-[#00F0FF]" />
                      Characters Detected
                    </h4>
                    <div className="space-y-2">
                      {storyAnalysis.characters.map((char, i) => (
                        <div key={i} className="text-sm text-muted-foreground">
                          <span className="text-white font-medium">{char.name || `Character ${i+1}`}:</span>{" "}
                          {char.appearance} {char.clothing && `wearing ${char.clothing}`}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Setting */}
                {storyAnalysis.setting && (
                  <div className="bg-secondary/30 rounded-lg p-3">
                    <h4 className="text-white font-medium mb-1">Setting</h4>
                    <p className="text-sm text-muted-foreground">
                      {storyAnalysis.setting.location}{storyAnalysis.setting.time_period ? ` - ${storyAnalysis.setting.time_period}` : ''}{storyAnalysis.setting.time_of_day ? ` (${storyAnalysis.setting.time_of_day})` : ''}
                    </p>
                    {storyAnalysis.setting.atmosphere && (
                      <p className="text-xs text-muted-foreground/70 mt-1">{storyAnalysis.setting.atmosphere}</p>
                    )}
                  </div>
                )}

                {/* Story Summary - new field */}
                {storyAnalysis.story_summary && (
                  <div className="bg-secondary/30 rounded-lg p-3">
                    <h4 className="text-white font-medium mb-1">Story Summary</h4>
                    <p className="text-sm text-muted-foreground">{storyAnalysis.story_summary}</p>
                  </div>
                )}

                {/* Current Action / Plot Points */}
                {(storyAnalysis.current_action || storyAnalysis.plot_points) && (
                  <div className="bg-secondary/30 rounded-lg p-3">
                    <h4 className="text-white font-medium mb-1">Where We Left Off</h4>
                    <p className="text-sm text-muted-foreground">
                      {storyAnalysis.current_action || storyAnalysis.plot_points?.ending || 'Analysis available'}
                    </p>
                  </div>
                )}

                {/* Suggestions */}
                {storyAnalysis.continuation_suggestions?.length > 0 && (
                  <div>
                    <h4 className="text-white font-medium mb-2">Suggested Continuations</h4>
                    <div className="grid gap-2">
                      {storyAnalysis.continuation_suggestions.map((suggestion, i) => {
                        // Handle both old format (string) and new format (object)
                        const isObject = typeof suggestion === 'object';
                        const title = isObject ? suggestion.title : `Option ${i + 1}`;
                        const premise = isObject ? suggestion.premise : suggestion;
                        const promptText = isObject ? (suggestion.prompt || suggestion.premise) : suggestion;
                        
                        return (
                          <button
                            key={i}
                            onClick={() => { setContinuationPrompt(promptText); setChosenSuggestion(isObject ? suggestion : null); }}
                            className={`text-left p-3 rounded-lg border text-sm transition-all ${
                              continuationPrompt === promptText
                                ? "border-[#00F0FF] bg-[#00F0FF]/10"
                                : "border-border/50 hover:border-border"
                            }`}
                            data-testid={`continuation-suggestion-${i}`}
                          >
                            {isObject ? (
                              <>
                                <div className="text-[#00F0FF] font-medium mb-1">{title}</div>
                                <div className="text-muted-foreground">{premise}</div>
                              </>
                            ) : (
                              <span className="text-muted-foreground">{suggestion}</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {continuationPrompt.trim() && (
                  <ImproveScenePanel key={chosenSuggestion?.title || "custom"} videoId={selectedVideo?.id} prompt={continuationPrompt} title={chosenSuggestion?.title}
                    duration={Math.round(Number(seriesInfo?.settings?.duration) || 15)} onImproved={setContinuationPrompt} />
                )}

                {/* Custom Continuation Prompt */}
                <div>
                  <Label className="text-white mb-2 block">What Happens Next?</Label>
                  <Textarea
                    value={continuationPrompt}
                    onChange={(e) => setContinuationPrompt(e.target.value)}
                    placeholder="Describe what happens in the next episode..."
                    data-testid="continuation-prompt-textarea"
                    className="bg-secondary/50 border-border/50 min-h-[100px]"
                  />
                </div>

                {/* Continue to Video Creator Button */}
                <Button
                  onClick={generateStoryContinuation}
                  disabled={!continuationPrompt.trim()}
                  className="w-full bg-gradient-to-r from-[#00F0FF] to-[#A855F7] h-12"
                  data-testid="continue-to-creator-btn"
                >
                  <Play className="w-4 h-4 mr-2" /> Continue to Video Creator
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  You&apos;ll be able to edit the prompt and settings before generating
                </p>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {openSeriesId && seriesView.stacks[openSeriesId] && (
        <SeriesDialog series={seriesView.stacks[openSeriesId]} onClose={() => setOpenSeriesId(null)}
          onMerged={() => { setOpenSeriesId(null); fetchVideos(); }} />
      )}

      {/* Save as Template Dialog */}
      <Dialog open={!!saveTemplateVideo} onOpenChange={(open) => !open && setSaveTemplateVideo(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bookmark className="w-5 h-5 text-[#00FF94]" />
              Save as Template
            </DialogTitle>
            <DialogDescription>
              Save this video's settings as a reusable template
            </DialogDescription>
          </DialogHeader>
          
          {saveTemplateVideo && (
            <div className="space-y-4 py-4">
              {/* Template Name */}
              <div>
                <Label htmlFor="template-name">Template Name *</Label>
                <input
                  id="template-name"
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="My Viral Script Template"
                  className="w-full mt-1 px-3 py-2 bg-secondary/50 border border-border/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00F0FF]/50"
                />
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="template-desc">Description</Label>
                <Textarea
                  id="template-desc"
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  placeholder="A template that works great for..."
                  className="mt-1 min-h-[60px]"
                />
              </div>

              {/* Video Settings Preview */}
              <div className="p-3 bg-secondary/30 rounded-lg border border-border/30">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Settings to save</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Duration:</span>{" "}
                    <span className="text-white">{saveTemplateVideo.duration || 15}s</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Mode:</span>{" "}
                    <span className="text-white capitalize">{saveTemplateVideo.video_mode || "fast"}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Model:</span>{" "}
                    <span className="text-white capitalize">{saveTemplateVideo.video_model || "kling"}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Voice:</span>{" "}
                    <span className="text-white capitalize">{saveTemplateVideo.voice_option || "none"}</span>
                  </div>
                </div>
                {saveTemplateVideo.script && (
                  <div className="mt-2 pt-2 border-t border-border/30">
                    <span className="text-muted-foreground text-xs">Script preview:</span>
                    <p className="text-white text-xs mt-1 line-clamp-2">{saveTemplateVideo.script}</p>
                  </div>
                )}
              </div>

              {/* Public Toggle */}
              <div className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg">
                <div>
                  <p className="font-medium text-white text-sm">Share with Community</p>
                  <p className="text-xs text-muted-foreground">Make available to other users</p>
                </div>
                <button
                  type="button"
                  onClick={() => setTemplateIsPublic(!templateIsPublic)}
                  className={`w-10 h-6 rounded-full transition-colors ${
                    templateIsPublic ? "bg-[#00F0FF]" : "bg-secondary"
                  }`}
                >
                  <div className={`w-4 h-4 bg-white rounded-full transition-transform mx-1 ${
                    templateIsPublic ? "translate-x-4" : "translate-x-0"
                  }`} />
                </button>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setSaveTemplateVideo(null)}
                  className="flex-1"
                  disabled={savingTemplate}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveAsTemplate}
                  disabled={savingTemplate || !templateName.trim()}
                  className="flex-1 bg-[#00FF94] hover:bg-[#00FF94]/90 text-black font-bold"
                >
                  {savingTemplate ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Template
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
